/**
 * useEditableNodes - Reactive view of relationship nodes with pending ops
 *
 * Combines GraphStore reactivity (via useNodes) with editable pending ops,
 * providing a merged view that includes optimistic updates.
 *
 * ## Clean Architecture (LocalUUID Flow)
 *
 * This hook performs a SIMPLE MERGE with no filtering logic because:
 * - **GraphStore** = confirmed server data only (never has items with _localUuid)
 * - **pendingOps** = optimistic data (items with local- IDs awaiting server confirmation)
 *
 * The LocalUUID flow prevents duplicates at the source:
 * 1. User adds item → assigned local UUID → registered in pendingLocalUuids
 * 2. DAG patch arrives with _localUuid → buffered (NOT added to GraphStore)
 * 3. Modify response arrives → buffered DAG data promoted to GraphStore → pendingOps cleared
 *
 * Result: No duplicates possible, no filtering needed!
 *
 * @example
 * ```tsx
 * function ChatView({ thread }: { thread: Thread }) {
 *   const editable = useEditable(thread);
 *   const steps = useEditableNodes(editable?.steps);
 *
 *   const addStep = async (text: string) => {
 *     const step = new Step({ text, role: ChatRoleType.USER });
 *     editable.steps.add(step); // Appears immediately in steps
 *     await editable.save(); // Persist to server
 *   };
 *
 *   return <>{steps?.orderBy('created_at').value().map(s => <StepView key={s.id} step={s} />)}</>;
 * }
 * ```
 */

import { useMemo } from 'react';
import { useAtomValue, atom } from 'jotai';
import { graphStoreJotaiStore } from '@sourcepilot/framework/graph-store';
import { useNodes } from '@sourcepilot/framework/relationship-manager';
import { createMaterializedView, MaterializedView } from '@sourcepilot/framework/materialized-view';
import type { EditableRelationshipManager } from '@sourcepilot/framework/editable-relationship-manager';
// Note: isLocalUuid import removed - no longer needed with clean architecture

/**
 * useEditableNodes - Returns reactive merged view of GraphStore + pending ops
 *
 * Features:
 * - Subscribes to GraphStore changes via underlying RelationshipManager
 * - Subscribes to pending ops changes via EditableRelationshipManager's version atom
 * - Returns merged view: current nodes + pending adds - pending removes
 * - Provides MaterializedView API (.orderBy, .filter, .value)
 *
 * @param editableManager - EditableRelationshipManager from useEditable().relationship
 * @returns MaterializedView with merged data, or null if no manager provided
 */
export function useEditableNodes<T>(
  editableManager: EditableRelationshipManager<T> | null | undefined
): MaterializedView<T> | null {
  // Get underlying RelationshipManager for GraphStore reactivity
  const originalManager = editableManager?.getOriginalManager() ?? undefined;

  // Subscribe to GraphStore changes via useNodes
  // This gives us reactive access to the current state in GraphStore
  const graphStoreNodes = useNodes(originalManager);

  // Create stable fallback atom (only once)
  const fallbackAtom = useMemo(() => atom(0), []);

  // Subscribe to pending ops changes via version atom
  // This triggers re-render when add/remove/set is called
  const versionAtom = editableManager?.getVersionAtom() ?? fallbackAtom;
  const version = useAtomValue(versionAtom, { store: graphStoreJotaiStore });

  // Compute merged view: GraphStore nodes + pending adds - pending removes
  // IMPORTANT: Depend on `version` (the VALUE), not `versionAtom` (the atom reference)
  const mergedView = useMemo(() => {
    if (!editableManager || !graphStoreNodes) return null;

    const pendingOps = editableManager.getPendingOps();
    const added = ((pendingOps.add || []) as any[]).map((entry: any) => entry.target) as T[];
    const removedIds = new Set(
      ((pendingOps.remove || []) as any[]).map(r => r?.id).filter(Boolean)
    );

    // Get current nodes from GraphStore
    const currentNodes = graphStoreNodes.value();

    // Clean Architecture: Simple merge - no filtering needed!
    // GraphStore only contains confirmed server data (DAG patches with _localUuid are buffered)
    // pendingOps contains optimistic data (items with local- IDs)
    // No duplicates possible because:
    // 1. DAG patches with _localUuid are buffered, not added to GraphStore
    // 2. Only when modify response arrives do we promote to GraphStore AND clear pendingOps
    const mergedNodes = [
      ...currentNodes.filter((n: any) => !removedIds.has(n?.id)),
      ...added
    ];

    return createMaterializedView(mergedNodes);
  }, [editableManager, graphStoreNodes, version]);

  return mergedView;
}
